import React from "react";
import { formatAppDateTime } from "../utils/appDateTime";

const replicateWeekOptions = Array.from({ length: 10 }, (_, index) => {
  const week = index + 1;
  return {
    value: String(week),
    label: `${week} week${week === 1 ? "" : "s"}`,
  };
});

export function formatReplicateDateTime(value) {
  return formatAppDateTime(value, { emptyValue: "—" });
}

export function formatReplicateDuration(startDate, endDate, unpaidBreakMinutes) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const totalMinutes = Math.max(
    0,
    Math.round((end.getTime() - start.getTime()) / 60000),
  );
  const breakMinutes =
    unpaidBreakMinutes === true ||
    unpaidBreakMinutes === 1 ||
    unpaidBreakMinutes === "1"
      ? 30
      : Math.max(0, Number(unpaidBreakMinutes || 0));
  const netMinutes = Math.max(0, totalMinutes - breakMinutes);
  const hours = Math.floor(netMinutes / 60);
  const minutes = netMinutes % 60;
  const breakHours = Math.floor(breakMinutes / 60);
  const breakMins = breakMinutes % 60;
  const hoursPart = hours > 0 ? `${hours}h` : "";
  const minsPart = minutes > 0 ? `${minutes}m` : "";
  const durationText = [hoursPart, minsPart].filter(Boolean).join(" ") || "0h";
  if (!breakMinutes) return durationText;
  const breakHoursPart = breakHours > 0 ? `${breakHours}h` : "";
  const breakMinsPart = breakMins > 0 ? `${breakMins}m` : "";
  const breakText =
    [breakHoursPart, breakMinsPart].filter(Boolean).join(" ") || "0m";
  return `${durationText} after ${breakText} break`;
}

export default function StaffingRequestReplicateModal({
  open,
  onClose,
  replicateSourceRequest,
  replicateRows,
  replicateWeeks,
  replicateLoading,
  replicateSubmitting,
  hasArchivedPositions = false,
  allShiftsArchived = false,
  onWeeksChange,
  onRowChange,
  onSubmit,
  overlayZIndex = 50400,
}) {
  if (!open) return null;

  const emptyMessage = replicateLoading
    ? "Loading shifts..."
    : allShiftsArchived
      ? "All shifts use archived positions and cannot be replicated. Create new shifts with active positions on the staffing request."
      : hasArchivedPositions
        ? "No replicable shifts remain (archived-position shifts were omitted)."
        : "No shifts available for replication.";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: overlayZIndex }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Staffing Request Replicate</h3>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700"
              onClick={onClose}
            >
              ✕
            </button>
          </div>

          <div className="text-sm text-gray-700 space-y-3">
            <div className="text-[28px] font-medium">
              Staffing request{" "}
              {replicateSourceRequest?.staff_request_id ||
                replicateSourceRequest?.id}
              {" • "}
              {replicateSourceRequest?.client_name ||
                replicateSourceRequest?.clientName ||
                "—"}
              {" • "}
              {replicateSourceRequest?.division_name ||
                replicateSourceRequest?.divisionName ||
                "—"}
            </div>
            <div>
              All the properties of the existing staff request will be
              replicated to a new staff request. You may then modify the new
              staff request independently.
            </div>
            <div>
              The selected shifts will be replicated to a new staff request for
              a future week. You may also choose to replicate a historic staff
              assignment and confirm that staff&apos;s assignment.
            </div>
            <div>
              Include staff assignment is enabled if staff is available for the
              replicated shift.
            </div>
          </div>

          {hasArchivedPositions && (
            <div
              className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              role="status"
            >
              Some shifts on this staffing request use archived positions; those
              shifts are not shown here and will not be replicated. Create new
              shifts with active positions if needed.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Add Time
            </label>
            <select
              className="w-full border rounded px-3 py-2 bg-white max-w-md"
              value={replicateWeeks}
              onChange={(e) => onWeeksChange(e.target.value)}
              disabled={replicateLoading || replicateSubmitting}
            >
              {replicateWeekOptions.map((row) => (
                <option key={row.value} value={row.value}>
                  {row.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <h4 className="text-2xl font-medium">Shifts to replicate</h4>
            <div className="border rounded max-h-[46vh] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Include Shift</th>
                    <th className="px-3 py-2 text-left">Shift</th>
                    <th className="px-3 py-2 text-left">Scheduled</th>
                    <th className="px-3 py-2 text-left">
                      Include Staff Assignment
                    </th>
                    <th className="px-3 py-2 text-left">Confirmed</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {replicateRows.map((row) => {
                    const assignmentDisabled =
                      !row.assigned_account_id ||
                      row.staff_available === false;
                    return (
                      <tr key={row.shift_id}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={Boolean(row.includeShift)}
                            onChange={(e) =>
                              onRowChange(
                                row.shift_id,
                                "includeShift",
                                e.target.checked,
                              )
                            }
                            disabled={replicateSubmitting}
                          />
                        </td>
                        <td className="px-3 py-2 text-red-600">
                          {row.shift_id}
                          <div className="text-xs text-gray-500">
                            {row.position_name || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div>
                            {formatReplicateDateTime(row.replicated_start_date)}{" "}
                            -{" "}
                            {formatReplicateDateTime(row.replicated_end_date)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatReplicateDuration(
                              row.replicated_start_date,
                              row.replicated_end_date,
                              row.unpaid_break,
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <label
                            className={`inline-flex items-center gap-2 ${
                              assignmentDisabled
                                ? "text-gray-400"
                                : "text-gray-700"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(row.includeStaffAssignment)}
                              onChange={(e) =>
                                onRowChange(
                                  row.shift_id,
                                  "includeStaffAssignment",
                                  e.target.checked,
                                )
                              }
                              disabled={
                                replicateSubmitting || assignmentDisabled
                              }
                            />
                            <span>
                              {row.assigned_staff_name ||
                                (assignmentDisabled
                                  ? "Unassigned / unavailable"
                                  : "Unassigned")}
                            </span>
                          </label>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={Boolean(row.confirmed)}
                            onChange={(e) =>
                              onRowChange(
                                row.shift_id,
                                "confirmed",
                                e.target.checked,
                              )
                            }
                            disabled={
                              replicateSubmitting ||
                              !row.includeStaffAssignment ||
                              assignmentDisabled
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {replicateRows.length === 0 && (
                    <tr>
                      <td className="px-3 py-3 text-gray-500" colSpan={5}>
                        {emptyMessage}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={onClose}
              disabled={replicateSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded bg-sidebar-active text-white hover:bg-red-600 disabled:opacity-60"
              onClick={onSubmit}
              disabled={
                replicateLoading ||
                replicateSubmitting ||
                replicateRows.length === 0
              }
            >
              {replicateSubmitting ? "Replicating..." : "Replicate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
